import type { UserRole } from '@/api/models';
import { PATH } from '@/router/PATH';
import type { AuthMethod } from '@/utils/authSession';

/** Tab identity — also the `Tabs` selection key and the testid discriminator. */
export type RegistersTabId = 'fixed-assets' | 'salaries' | 'dividends';

export type RegistersTab = {
    id: RegistersTabId;
    /** Absolute route this tab owns; selecting the tab navigates here. */
    path: string;
    labelKey: string;
    /**
     * Roles that may see this tab at all. Absent means every caller who reached
     * the Registers shell — which is already `OrgAdmin | BranchAdmin`, gated on
     * the sidebar entry.
     *
     * Only Salaries constrains this, and it is not a UI preference: **every
     * route under `api/v1/ledger/salaries` is `OrgAdmin`, reads included**,
     * because the register is org-wide and carries every colleague's salary,
     * personal number and net pay. Showing a BranchAdmin a tab whose every
     * request can only 403 is worse than not showing it. See `salariesAccess`.
     */
    roles?: UserRole[];
};

/**
 * The register tabs, in display order — the same order they had as sidebar
 * items. The first is the default: the bare `/accounting/registers` renders it
 * through an index route, so the tab bar reads the same whether the URL carries
 * the `fixed-assets` segment or not.
 */
export const REGISTERS_TABS: RegistersTab[] = [
    {
        id: 'fixed-assets',
        path: PATH.ACCOUNTING.REGISTERS.FIXED_ASSETS,
        labelKey: 'accounting.registers.tabs.fixedAssets',
    },
    {
        id: 'salaries',
        path: PATH.ACCOUNTING.REGISTERS.SALARIES,
        labelKey: 'accounting.registers.tabs.salaries',
        roles: ['OrgAdmin'],
    },
    {
        id: 'dividends',
        path: PATH.ACCOUNTING.REGISTERS.DIVIDENDS,
        labelKey: 'accounting.registers.tabs.dividends',
        // Same rule as Salaries, and for a closer reason than it looks: every
        // route under `api/v1/ledger/dividends` is `OrgAdmin` too, because the
        // register names who owns the company and in what proportion, and the
        // summary carries the entity's retained earnings. See `dividendsAccess`.
        roles: ['OrgAdmin'],
    },
];

export const DEFAULT_REGISTERS_TAB_ID: RegistersTabId = 'fixed-assets';

/**
 * Which tab the current URL selects. The bare shell path and anything
 * unrecognised fall back to the default tab, so the bar is never rendered with
 * nothing selected.
 */
export const resolveRegistersTabId = (pathname: string): RegistersTabId => {
    const matched = REGISTERS_TABS.find(
        (tab) => pathname === tab.path || pathname.startsWith(`${tab.path}/`),
    );

    return matched?.id ?? DEFAULT_REGISTERS_TAB_ID;
};

/** Route target for a tab id; unknown ids land on the default tab's route. */
export const resolveRegistersTabPath = (id: string): string => {
    const matched = REGISTERS_TABS.find((tab) => tab.id === id);

    return matched?.path ?? PATH.ACCOUNTING.REGISTERS.FIXED_ASSETS;
};

/**
 * Whether a caller's session may see a tab.
 *
 * The role is only half of it: the API's `OrgAdmin` policy also requires
 * `auth_method=password`, so an OrgAdmin holding a PIN-issued kiosk token 403s
 * exactly as a BranchAdmin does. Failing closed on an unknown auth method is
 * deliberate — a tab that should have been hidden is a worse mistake than a tab
 * briefly missing.
 */
const isTabVisible = (
    tab: RegistersTab,
    role: UserRole | null,
    authMethod: AuthMethod | null,
): boolean => {
    if (!tab.roles) {
        return true;
    }
    return (
        authMethod === 'password' && role !== null && tab.roles.includes(role)
    );
};

/**
 * The tab bar for this session, and which tab the URL selects.
 *
 * The selected tab is **always** in the returned list, even when the caller's
 * role would hide it. That is not a loophole: each tab's panel is what renders
 * the routed `<Outlet />`, so dropping the selected tab would leave a caller who
 * followed a direct link with a blank screen instead of the page's own
 * explanation of why payroll is unavailable to them. The page refuses; the bar
 * only stops advertising. Navigate away and the tab is gone.
 */
export const registersTabsFor = (
    pathname: string,
    role: UserRole | null,
    authMethod: AuthMethod | null,
): { tabs: RegistersTab[]; selectedTabId: RegistersTabId } => {
    const selectedTabId = resolveRegistersTabId(pathname);

    return {
        tabs: REGISTERS_TABS.filter(
            (tab) =>
                tab.id === selectedTabId || isTabVisible(tab, role, authMethod),
        ),
        selectedTabId,
    };
};
